<template>
  <v-dialog
    :model-value="modelValue"
    max-width="1280"
    scrollable
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="audit-detail-dialog">
      <v-card-title class="audit-detail-header pa-4 pa-md-5">
        <div class="d-flex align-start ga-3 w-100">
          <v-avatar color="white" variant="tonal" size="44">
            <v-icon color="white">mdi-shield-search-outline</v-icon>
          </v-avatar>
          <div class="min-width-0">
            <div class="text-h6 text-truncate">{{ title }}</div>
            <div class="text-caption audit-header-muted">
              {{ activity.entity_label || "Audit activity" }}
              <template v-if="activity.root_id !== null && activity.root_id !== undefined"> · ID {{ activity.root_id }}</template>
            </div>
          </div>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" color="white" title="Close" @click="close" />
        </div>
      </v-card-title>

      <v-card-text class="pa-0">
        <div v-if="loading" class="pa-5">
          <v-skeleton-loader type="article, table" />
        </div>

        <div v-else-if="error" class="pa-5">
          <v-alert type="error" variant="tonal">{{ error }}</v-alert>
        </div>

        <template v-else-if="detail">
          <div class="audit-summary pa-4 pa-md-5">
            <div class="d-flex flex-wrap ga-2 mb-4">
              <v-chip v-if="activity.module_label" color="indigo" variant="tonal" size="small" prepend-icon="mdi-view-grid-outline">
                {{ activity.module_label }}
              </v-chip>
              <v-chip
                v-for="event in activity.events || []"
                :key="event"
                :color="eventColor(event)"
                variant="tonal"
                size="small"
              >
                {{ humanize(event) }} {{ activity.event_counts?.[event] || "" }}
              </v-chip>
              <v-chip color="blue-grey" variant="outlined" size="small" prepend-icon="mdi-delta">
                {{ activity.changes_count || 0 }} change{{ activity.changes_count === 1 ? "" : "s" }}
              </v-chip>
            </div>

            <v-row dense>
              <v-col cols="12" sm="6" lg="3">
                <div class="audit-fact">
                  <v-icon color="primary" size="20">mdi-account-outline</v-icon>
                  <div><span>Changed by</span><strong>{{ activity.user?.label || "System" }}</strong></div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" lg="3">
                <div class="audit-fact">
                  <v-icon color="primary" size="20">mdi-clock-outline</v-icon>
                  <div><span>Last change</span><strong>{{ formatDateTime(activity.last_event_at) }}</strong></div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" lg="3">
                <div class="audit-fact">
                  <v-icon color="primary" size="20">mdi-office-building-outline</v-icon>
                  <div><span>Company</span><strong>{{ activity.company?.label || "—" }}</strong></div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" lg="3">
                <div class="audit-fact">
                  <v-icon color="primary" size="20">mdi-source-branch</v-icon>
                  <div><span>Branch</span><strong>{{ activity.branch?.label || "—" }}</strong></div>
                </div>
              </v-col>
            </v-row>
          </div>

          <v-tabs v-model="tab" color="primary" class="audit-detail-tabs" show-arrows>
            <v-tab value="grouped" prepend-icon="mdi-file-tree-outline">Master & Details</v-tab>
            <v-tab value="timeline" prepend-icon="mdi-timeline-clock-outline">Timeline</v-tab>
            <v-tab value="context" prepend-icon="mdi-information-outline">Request Context</v-tab>
          </v-tabs>
          <v-divider />

          <v-window v-model="tab">
            <v-window-item value="grouped">
              <div class="pa-4 pa-md-5 audit-detail-content">
                <v-card class="audit-entity-card mb-4" elevation="0">
                  <v-card-title class="d-flex flex-wrap align-center ga-2 text-subtitle-1">
                    <v-icon color="primary">mdi-database-outline</v-icon>
                    <span>Master: {{ master.entity_label || activity.entity_label }}</span>
                    <v-chip size="x-small" variant="outlined">{{ master.record_label || title }}</v-chip>
                  </v-card-title>
                  <v-divider />
                  <v-card-text>
                    <v-alert v-if="!master.changes?.length" type="info" variant="tonal" density="compact">
                      This request did not directly change the master row.
                    </v-alert>
                    <v-expansion-panels v-else multiple variant="accordion">
                      <AuditChangeSet
                        v-for="change in master.changes"
                        :key="change.id"
                        :entry="change"
                      />
                    </v-expansion-panels>
                  </v-card-text>
                </v-card>

                <div class="d-flex align-center ga-2 mb-3">
                  <v-icon color="primary">mdi-table-multiple</v-icon>
                  <h3 class="text-subtitle-1">Detail records</h3>
                  <v-chip size="x-small" color="primary" variant="tonal">{{ detailRecordCount }}</v-chip>
                </div>

                <v-alert v-if="!details.length" type="info" variant="tonal">
                  No child/detail rows were changed in this activity.
                </v-alert>

                <v-card
                  v-for="group in details"
                  :key="group.auditable_type"
                  class="audit-entity-card mb-4"
                  elevation="0"
                >
                  <v-card-title class="d-flex flex-wrap align-center ga-2 text-subtitle-1">
                    <v-icon color="teal">mdi-table-row-plus-after</v-icon>
                    <span>{{ group.entity_label || group.entity }}</span>
                    <v-chip size="x-small" color="teal" variant="tonal">
                      {{ group.records?.length || 0 }} record{{ group.records?.length === 1 ? "" : "s" }}
                    </v-chip>
                  </v-card-title>
                  <v-divider />
                  <v-card-text>
                    <section v-for="record in group.records || []" :key="String(record.record_id)" class="audit-record-section">
                      <div class="d-flex flex-wrap align-center ga-2 mb-2">
                        <strong class="text-body-2">{{ record.record_label }}</strong>
                        <span class="text-caption text-medium-emphasis">ID {{ record.record_id }}</span>
                      </div>
                      <v-expansion-panels multiple variant="accordion">
                        <AuditChangeSet
                          v-for="change in record.changes || []"
                          :key="change.id"
                          :entry="change"
                        />
                      </v-expansion-panels>
                    </section>
                  </v-card-text>
                </v-card>
              </div>
            </v-window-item>

            <v-window-item value="timeline">
              <div class="pa-4 pa-md-5 audit-detail-content">
                <div v-if="timeline.length" class="audit-timeline">
                  <article v-for="entry in timeline" :key="entry.id" class="audit-timeline-item">
                    <div class="audit-timeline-marker" :class="`audit-event-${entry.event}`">
                      <v-icon size="17" color="white">{{ eventIcon(entry.event) }}</v-icon>
                    </div>
                    <div class="audit-timeline-card">
                      <div class="d-flex flex-wrap align-center ga-2 mb-2">
                        <v-chip :color="eventColor(entry.event)" size="small" variant="tonal">{{ humanize(entry.event) }}</v-chip>
                        <strong>{{ entry.entity_label || entry.entity }}</strong>
                        <span class="text-body-2 text-medium-emphasis">{{ entry.record_label }}</span>
                        <v-spacer />
                        <span class="text-caption text-medium-emphasis">{{ formatDateTime(entry.created_at) }}</span>
                      </div>
                      <div v-if="entry.changes?.length" class="d-flex flex-wrap ga-1">
                        <v-chip
                          v-for="change in entry.changes.slice(0, 6)"
                          :key="change.field"
                          size="x-small"
                          color="blue-grey"
                          variant="outlined"
                        >
                          {{ change.field_label || humanize(change.field) }}
                        </v-chip>
                        <v-chip v-if="entry.changes.length > 6" size="x-small" variant="text">
                          +{{ entry.changes.length - 6 }} more
                        </v-chip>
                      </div>
                    </div>
                  </article>
                </div>
                <v-alert v-else type="info" variant="tonal">No audit events are available.</v-alert>
              </div>
            </v-window-item>

            <v-window-item value="context">
              <div class="pa-4 pa-md-5 audit-detail-content">
                <v-card class="audit-entity-card" elevation="0">
                  <v-table density="comfortable">
                    <tbody>
                      <tr v-for="row in contextRows" :key="row.label">
                        <th>{{ row.label }}</th>
                        <td><code v-if="row.code">{{ row.value }}</code><span v-else>{{ row.value }}</span></td>
                      </tr>
                    </tbody>
                  </v-table>
                </v-card>
              </div>
            </v-window-item>
          </v-window>
        </template>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-4 px-md-5 py-3">
        <span v-if="activity.uuid" class="text-caption text-medium-emphasis text-truncate">Activity {{ activity.uuid }}</span>
        <v-spacer />
        <v-btn variant="tonal" color="blue-grey" @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import AuditChangeSet from "./AuditChangeSet.vue";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  detail: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  error: { type: String, default: "" },
});

const emit = defineEmits(["update:modelValue"]);
const tab = ref("grouped");

const activity = computed(() => props.detail?.activity || {});
const master = computed(() => props.detail?.master || {});
const details = computed(() => props.detail?.details || []);
const timeline = computed(() => props.detail?.timeline || []);
const title = computed(() => activity.value.root_label || master.value.record_label || activity.value.entity_label || "Audit activity");
const detailRecordCount = computed(() => details.value.reduce((total, group) => total + (group.records?.length || 0), 0));

const contextRows = computed(() => [
  { label: "Activity UUID", value: activity.value.uuid || "—", code: true },
  { label: "Request UUID", value: activity.value.request_uuid || "—", code: true },
  { label: "Source", value: humanize(activity.value.source || "—") },
  { label: "HTTP method", value: activity.value.http_method || "—", code: true },
  { label: "Route", value: activity.value.route || "—", code: true },
  { label: "URL", value: activity.value.url || "—", code: true },
  { label: "IP address", value: activity.value.ip_address || "—", code: true },
  { label: "Root model", value: activity.value.root_type || "—", code: true },
  { label: "First event", value: formatDateTime(activity.value.first_event_at) },
  { label: "Last event", value: formatDateTime(activity.value.last_event_at) },
]);

const eventColors = { created: "success", updated: "primary", deleted: "error", restored: "warning" };
const eventIcons = {
  created: "mdi-plus",
  updated: "mdi-pencil",
  deleted: "mdi-delete-outline",
  restored: "mdi-backup-restore",
};
const eventColor = (event) => eventColors[event] || "blue-grey";
const eventIcon = (event) => eventIcons[event] || "mdi-history";
const humanize = (value) => String(value || "Unknown").replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
}

function close() {
  emit("update:modelValue", false);
}

watch(() => props.modelValue, (isOpen) => {
  if (isOpen) tab.value = "grouped";
});
</script>

<style scoped>
.audit-detail-dialog {
  max-height: 92vh;
  border-radius: 16px !important;
}

.audit-detail-header {
  background: linear-gradient(135deg, #263f59 0%, #315f73 100%);
  color: #fff;
}

.audit-header-muted {
  color: rgba(255, 255, 255, 0.75);
}

.min-width-0 {
  min-width: 0;
}

.audit-summary {
  background: #f7f9fa;
  border-bottom: 1px solid #e4e9ec;
}

.audit-fact {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  height: 100%;
  padding: 12px;
  background: #fff;
  border: 1px solid #e5eaed;
  border-radius: 10px;
}

.audit-fact span,
.audit-fact strong {
  display: block;
}

.audit-fact span {
  color: #718087;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.audit-fact strong {
  margin-top: 2px;
  color: #2d414a;
  font-size: 0.84rem;
  overflow-wrap: anywhere;
}

.audit-detail-tabs {
  background: #fff;
}

.audit-detail-content {
  min-height: 260px;
  background: #f7f9fa;
}

.audit-entity-card {
  border: 1px solid #dfe6ea;
  border-radius: 12px;
}

.audit-record-section + .audit-record-section {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px dashed #d9e0e4;
}

.audit-timeline {
  position: relative;
  padding-left: 26px;
}

.audit-timeline::before {
  position: absolute;
  top: 10px;
  bottom: 10px;
  left: 11px;
  width: 2px;
  background: #d9e1e5;
  content: "";
}

.audit-timeline-item {
  position: relative;
  padding-bottom: 16px;
}

.audit-timeline-marker {
  position: absolute;
  top: 12px;
  left: -26px;
  z-index: 1;
  display: grid;
  width: 24px;
  height: 24px;
  place-items: center;
  background: #607d8b;
  border-radius: 50%;
  box-shadow: 0 0 0 4px #f7f9fa;
}

.audit-event-created { background: #2e7d32; }
.audit-event-updated { background: #1976d2; }
.audit-event-deleted { background: #d32f2f; }
.audit-event-restored { background: #ed6c02; }

.audit-timeline-card {
  padding: 14px;
  background: #fff;
  border: 1px solid #e0e6e9;
  border-radius: 10px;
}

.audit-entity-card th {
  width: 190px;
  color: #53656e;
  text-align: left;
}

.audit-entity-card code {
  color: #334e5b;
  font-size: 0.78rem;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

@media (max-width: 700px) {
  .audit-detail-dialog {
    max-height: 100vh;
    border-radius: 0 !important;
  }

  .audit-entity-card th {
    width: 120px;
  }
}
</style>
